import { Button } from '../catalyst/button';
import { BugAntIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogBody, DialogTitle } from '../catalyst/dialog';
import AuthGuard from './AuthGuard';
import { CollapsibleYamlView } from './CollapsibleYamlView';
import { Input } from '../catalyst/input';

export function DebugObject({ object }: { object: unknown }) {
  const [showDebug, setShowDebug] = useState(false);
  const [delayedHide, setDelayedHide] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (showDebug) {
      setDelayedHide(true);

      return;
    }

    const timeout = setTimeout(() => {
      setDelayedHide(false);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [showDebug]);

  useEffect(() => {
    const abortController = new AbortController();
    window.addEventListener(
      'keydown',
      (e) => {
        if (e.metaKey && e.key === 'd') {
          setShowDebug(true);
        }
      },
      {
        signal: abortController.signal,
      },
    );

    return () => {
      abortController.abort();
    };
  }, []);

  const filterredObject = useMemo(() => {
    if (!search) {
      return object;
    }

    return filterObject(object, search);
  }, [search, object]);

  return (
    <AuthGuard roles={['admin']}>
      <Button outline className="inline-flex items-center" onClick={() => setShowDebug(!showDebug)}>
        <BugAntIcon className="size-4" />
        Debug
      </Button>

      <Dialog open={showDebug} onClose={() => setShowDebug(false)} size="5xl">
        <DialogTitle className="flex justify-between items-center">
          Debug Object
          <Button outline onClick={() => setShowDebug(false)}>
            <XMarkIcon className="size-4" />
          </Button>
        </DialogTitle>
        <DialogBody>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} className="mb-4" placeholder="Search" />
          {(showDebug || delayedHide) && <CollapsibleYamlView data={filterredObject} />}
        </DialogBody>
      </Dialog>
    </AuthGuard>
  );
}

const filterObject = (target: unknown, search: string): unknown => {
  const needle = search.toLowerCase();

  if (Array.isArray(target)) {
    const value = target.map((item) => filterObject(item, search)).filter((item) => item !== undefined);
    return value.length > 0 ? value : undefined;
  }

  if (typeof target === 'object' && target !== null) {
    const entries = Object.entries(target).flatMap(([key, value]): [string, unknown][] => {
      if (key.toLowerCase().includes(needle)) {
        return [[key, value]];
      }

      const newValue = filterObject(value, search);
      return newValue !== undefined ? [[key, newValue]] : [];
    });

    if (entries.length === 0) {
      return undefined;
    }

    return Object.fromEntries(entries);
  }

  return String(target).toLowerCase().includes(needle) ? target : undefined;
};
